import type { ApiResponse, Mention } from '@fluenceur/types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

/**
 * Fetch a specific mention by ID
 * @param mentionId - The ID of the mention
 * @returns Promise with mention data
 */
const fetchMention = async (mentionId: string): Promise<Mention> => {
  const response = await api.get<ApiResponse<Mention>>(`/dashboard/mentions/${mentionId}`);
  return response.data.data as Mention;
};

interface UseMentionQueryOptions {
  mentionId: string | null | undefined;
  enabled?: boolean;
}

const MENTION_QUERY_KEY_PREFIX = 'mention';

export const useMentionQuery = ({
  mentionId,
  enabled = true,
}: UseMentionQueryOptions): UseQueryResult<Mention, Error> => {
  return useQuery<Mention, Error>({
    queryKey: [MENTION_QUERY_KEY_PREFIX, mentionId],
    queryFn: () => {
      if (!mentionId) {
        return Promise.reject(new Error('Mention ID is required.'));
      }
      return fetchMention(mentionId);
    },
    enabled: !!mentionId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

// Export the fetch function for use in other hooks
export { fetchMention };
